import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["super_admin", "manager", "user"];
const ROLE_LABEL = { super_admin: "Super Admin", manager: "Manager", user: "User" };

function UserDialog({ trigger, initial, departments, onSaved }) {
  const editing = !!initial;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || { email: "", password: "", name: "", role: "user", title: "", department_id: "", license_active: true, is_fee_earner: true });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${initial.id}`, {
          name: form.name, role: form.role, title: (form.title || "").trim(),
          department_id: form.department_id || null, license_active: form.license_active,
          is_fee_earner: !!form.is_fee_earner,
        });
      } else {
        await api.post("/users", { ...form, title: (form.title || "").trim(), department_id: form.department_id || null });
      }
      toast.success(editing ? "User updated" : "User created");
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-none sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading tracking-tight">{editing ? "Edit user" : "Add user"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none mt-1" data-testid="user-name" /></div>
          {!editing && <>
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-none mt-1" data-testid="user-email" /></div>
            <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Temp password</Label><Input value={form.password} onChange={(e) => set("password", e.target.value)} className="rounded-none mt-1" data-testid="user-password" /></div>
          </>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}><SelectTrigger className="rounded-none mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}</SelectContent></Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Commission</Label>
              <Select value={form.is_fee_earner ? "yes" : "no"} onValueChange={(v) => set("is_fee_earner", v === "yes")}><SelectTrigger className="rounded-none mt-1" data-testid="user-fee-earner"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="yes">Fee earner</SelectItem><SelectItem value="no">Non-fee earner</SelectItem></SelectContent></Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Title / Designation</Label>
            <Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} className="rounded-none mt-1" data-testid="user-title" placeholder="e.g. Senior Associate" />
            <p className="text-[11px] text-muted-foreground mt-1">Synced from Microsoft 365. Only <b>Fee earners</b> appear on the Commission page.</p>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Department</Label>
              <Select value={form.department_id || "none"} onValueChange={(v) => set("department_id", v === "none" ? "" : v)}><SelectTrigger className="rounded-none mt-1" data-testid="user-dept"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div className="flex items-center justify-between border border-border px-3 py-2">
            <span className="text-sm">License active</span>
            <Switch checked={form.license_active} onCheckedChange={(v) => set("license_active", v)} data-testid="user-license" />
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={saving} className="rounded-none w-full" data-testid="user-save">{saving ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sortDir, setSortDir] = useState("asc");
  const isAdmin = user?.role === "super_admin";

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/departments").then((r) => setDepartments(r.data));
    setSelected(new Set());
  };
  useEffect(load, []);

  // Rows the admin may delete = everyone except themselves.
  const selectableIds = users.filter((u) => u.id !== user?.id).map((u) => u.id);
  const sortedUsers = [...users].sort((a, b) => {
    const c = (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
    return sortDir === "asc" ? c : -c;
  });
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggleOne = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));

  const syncM365 = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/m365/sync");
      toast.success(data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSyncing(false); }
  };

  const deleteSelected = async () => {
    setDeleting(true);
    try {
      const { data } = await api.post("/users/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${data.deleted} user${data.deleted === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tighter">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">{isAdmin ? "All firm users." : "Users in your department."}</p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2">
            {selected.size > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="rounded-none gap-2 text-destructive border-destructive/40 hover:bg-destructive/10" data-testid="delete-selected-btn">
                    <Trash2 className="h-4 w-4" /> Delete selected ({selected.size})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-none">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {selected.size} user{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently removes the selected accounts. Their captured banking entries remain in the records but will show as an unknown earner. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-none" data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={deleteSelected} disabled={deleting} className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-confirm-btn">
                      {deleting ? "Deleting…" : "Delete"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button variant="outline" onClick={syncM365} disabled={syncing} className="rounded-none gap-2" data-testid="sync-m365-btn">
              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync Microsoft 365"}
            </Button>
            <UserDialog departments={departments} onSaved={load} trigger={<Button className="rounded-none gap-2" data-testid="add-user-btn"><Plus className="h-4 w-4" /> Add user</Button>} />
          </div>
        )}
      </div>

      <div className="border border-border bg-card" data-testid="users-table">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {isAdmin && <TableHead className="w-10"><Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" data-testid="select-all-users" /></TableHead>}
              <TableHead>
                <button type="button" onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))} className="inline-flex items-center gap-1 hover:text-brand transition-colors" data-testid="sort-users-name">
                  Name <span className="text-[10px]">{sortDir === "asc" ? "▲" : "▼"}</span>
                </button>
              </TableHead>
              <TableHead>Email</TableHead><TableHead>Role</TableHead>
              <TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Department</TableHead>
              <TableHead>Status</TableHead>{isAdmin && <TableHead></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedUsers.map((u) => {
              const isSelf = u.id === user?.id;
              return (
              <TableRow key={u.id} className="hover:bg-secondary" data-testid={`user-row-${u.id}`} data-state={selected.has(u.id) ? "selected" : undefined}>
                {isAdmin && <TableCell>{isSelf ? <span className="text-[10px] text-muted-foreground">you</span> : <Checkbox checked={selected.has(u.id)} onCheckedChange={() => toggleOne(u.id)} aria-label={`Select ${u.name}`} data-testid={`select-user-${u.id}`} />}</TableCell>}
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-muted-foreground text-xs font-mono">{u.email}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-none text-[10px]">{ROLE_LABEL[u.role]}</Badge></TableCell>
                <TableCell>{u.title}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`rounded-none text-[10px] ${u.is_fee_earner ? "text-brand border-brand/30" : "text-muted-foreground border-border"}`} data-testid={`user-type-${u.id}`}>
                    {u.is_fee_earner ? "Fee earner" : "Non-fee earner"}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">{u.department_name || "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`rounded-none text-[10px] ${u.license_active ? "text-positive border-positive/30" : "text-destructive border-destructive/30"}`}>
                    {u.license_active ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                {isAdmin && <TableCell>
                  <UserDialog initial={u} departments={departments} onSaved={load} trigger={<button className="text-muted-foreground hover:text-foreground" data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></button>} />
                </TableCell>}
              </TableRow>
            );})}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
